// WHICH SORTS THE PORTAL-ASSET LISTS CAN RUN — PURE. THE SINGLE LIST of (field, direction) pairs that have a
// declared composite-index family; the list builders refuse anything else, so a sort the UI offers can never
// answer FAILED_PRECONDITION in production.
//
// ⚠️⚠️ ONE INDEX PER (FIELD, DIRECTION) — NOT one per field. The 2026-10-12 brief assumed a query and its exact
// reverse are served by one index. Firestore's own index-overview page says otherwise: running "the same
// queries but with a descending sort order" needs "an additional index in the descending direction"
// (read 2026-10-12) AND OBSERVED against production by `npm run probe:sort-indexes`: the ascending/ascending reverse query threw FAILED_PRECONDITION (code 9), as did the mixed-direction control. So an
// ascending-only Name index serves A→Z and NOT Z→A. To add a direction, add the pair here; the index
// generator (scripts/generate-portal-asset-indexes.ts) and the enumerating test follow, with no other edit.
// Enabled (2026-10-13): createdAt desc (default) AND asc (oldest first), sortName asc, sizeBytes asc AND desc (largest
// first). NOT enabled: sortName desc (Z→A). Each pair is +65 live-list indexes (search needs none: it orders by
// createdAt desc only). ⚠️ A field can now have BOTH directions, so an index's order is a property of the
// (field, direction) pair, never of the field alone.
//
// ⚠️ NAME and SIZE ORDER ARE NOT AVAILABLE TOGETHER WITH A DATE RANGE (staff list): Firestore requires a range
// filter's field to be the FIRST orderBy, and the range is on `createdAt`. The staff builder refuses the
// combination rather than emitting a query Firestore would reject.

export type AssetSortField = 'createdAt' | 'sortName' | 'sizeBytes';
export type AssetSortDir = 'asc' | 'desc';
export type AssetSort = { field: AssetSortField; dir: AssetSortDir };

/** The sorts with an index. First = the default (newest upload first), which is what every list did before sorting existed. */
export const INDEXED_SORTS: readonly AssetSort[] = [
  { field: 'createdAt', dir: 'desc' },
  { field: 'createdAt', dir: 'asc' },
  { field: 'sortName', dir: 'asc' },
  { field: 'sizeBytes', dir: 'asc' },
  { field: 'sizeBytes', dir: 'desc' },
];

export const DEFAULT_SORT: AssetSort = INDEXED_SORTS[0]!;

export function isIndexedSort(s: AssetSort): boolean {
  return INDEXED_SORTS.some((i) => i.field === s.field && i.dir === s.dir);
}

/** The index order that serves a query direction. ⚠️ Per (field, direction): the exact reverse needs its OWN index. */
export function indexOrderFor(dir: AssetSortDir): 'ASCENDING' | 'DESCENDING' {
  return dir === 'asc' ? 'ASCENDING' : 'DESCENDING';
}

// THE FOUR NAMED SORTS THE UI OFFERS (Run B, 2026-10-13). Named options, not a field plus a direction toggle: a toggle
// would advertise six combinations and serve four (`sortName desc` is the one nobody built an index for), and a
// greyed-out arrow is worse than an option that was never offered. The wire carries the id; every consumer derives
// the (field, direction) from this one table.
export const SORT_OPTIONS = [
  { id: 'newest', label: 'Newest first', sort: { field: 'createdAt', dir: 'desc' } },
  { id: 'oldest', label: 'Oldest first', sort: { field: 'createdAt', dir: 'asc' } },
  { id: 'name', label: 'Name (A–Z)', sort: { field: 'sortName', dir: 'asc' } },
  { id: 'largest', label: 'Largest first', sort: { field: 'sizeBytes', dir: 'desc' } },
] as const satisfies readonly { id: string; label: string; sort: AssetSort }[];

export type SortOptionId = (typeof SORT_OPTIONS)[number]['id'];
export const SORT_OPTION_IDS = SORT_OPTIONS.map((o) => o.id) as [SortOptionId, ...SortOptionId[]];

export function sortFromOption(id: SortOptionId | undefined): AssetSort {
  return SORT_OPTIONS.find((o) => o.id === id)?.sort ?? DEFAULT_SORT;
}

/** Name and Size cannot carry a date range (Firestore wants the range field first in orderBy). */
export function sortAllowsDateRange(id: SortOptionId): boolean {
  return sortFromOption(id).field === 'createdAt';
}
